import { FC, useEffect, useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { ITransferListItem } from "@miplatform/types";
import { toUpperCase } from "@miplatform/utils";
import { TransferList } from "@mibuildweb/components";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { MaterialCard } from "../../../../components/MaterialCard/MaterialCard";
import { IAddRequirementsData } from "../../schema/addRequirements.schema";
import { useRequirementAttachItemsViewModel } from "./useRequirementAttachItemsViewModel";

const getMaterialLabel = (material: any): string => {
  const partNumber = material.manufacturerPartNumber ?? "";
  const desc =
    material.category?.itemDescription ?? material.itemDescription ?? "";
  return `${toUpperCase(partNumber)} | ${toUpperCase(desc)}`;
};

export const RequirementAttachItemsTab: FC<RequirementAttachItemsTabProps> = ({
  form,
  extraTabProps,
}) => {
  const { isReadOnly = false } = extraTabProps || {};
  const { classes } = useStyles();
  const { control, setValue } = form;

  const materialIds: string[] =
    (useWatch({ control, name: "materialIds" }) as string[]) ?? [];
  const materialAreas: Record<string, string[]> =
    (useWatch({ control, name: "materialAreas" }) as Record<
      string,
      string[]
    >) ?? {};

  const {
    allMaterials,
    areaTypes,
    selectedMaterialId,
    loadMaterials,
    getAreaTypes,
    handleTabChange,
    getSelectedAreaIds,
    handleAddArea,
    handleRemoveArea,
    setSelectedMaterialId,
  } = useRequirementAttachItemsViewModel({ materialAreas, setValue });

  useEffect(() => {
    getAreaTypes();
    loadMaterials();
  }, []);

  const materials = useMemo(
    () => allMaterials.filter((m) => materialIds.includes(m.id)),
    [allMaterials, materialIds]
  );

  useEffect(() => {
    if (materials.length > 0 && !selectedMaterialId) {
      setSelectedMaterialId(materials[0].id);
    }
  }, [materials]);

  const selectedMaterial = materials.find((m) => m.id === selectedMaterialId);

  const selectedAreaIds = selectedMaterialId
    ? getSelectedAreaIds(selectedMaterialId)
    : [];

  const availableAreaItems: ITransferListItem[] = areaTypes
    .filter((a) => !selectedAreaIds.includes(a.id))
    .map((a) => ({ key: a.id, title: a.name }));

  const selectedAreaItems: ITransferListItem[] = areaTypes
    .filter((a) => selectedAreaIds.includes(a.id))
    .map((a) => ({ key: a.id, title: a.name }));

  return (
    <Box className={classes.container}>
      <Box className={classes.inner}>
        <ConfigAccordionHeader title="Attach Items to Areas" disableCollapse>
          <Typography variant="BD1" color="text.secondary" sx={{ mb: 1 }}>
            Select Areas for this Item.
          </Typography>

          <Tabs
            value={selectedMaterialId ?? false}
            onChange={(_, value) => handleTabChange(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            {materials.map((material) => (
              <Tab
                key={material.id}
                value={material.id}
                label={
                  <Box className={classes.tabLabel}>
                    {getMaterialLabel(material)}
                  </Box>
                }
                sx={{ textTransform: "none", maxWidth: 200, minWidth: 0 }}
              />
            ))}
          </Tabs>

          {selectedMaterial && (
            <Box sx={{ mb: 2 }}>
              <MaterialCard item={selectedMaterial} showPrice />
            </Box>
          )}

          {selectedMaterialId && (
            <TransferList
              availableItems={availableAreaItems}
              selectedItems={selectedAreaItems}
              availableTitle="Available Areas"
              selectedTitle="Selected Areas"
              showAvailableItemsSearch
              availableSearchPlaceholder="Search"
              emptySelectedListMessage="Selected areas will show here."
              onAddItem={(key: string) =>
                handleAddArea(selectedMaterialId, key)
              }
              onRemoveItem={(key: string) =>
                handleRemoveArea(selectedMaterialId, key)
              }
              renderItemTitle={(item: ITransferListItem) => (
                <Typography variant="HD2" color="primary">
                  {item.title}
                </Typography>
              )}
              disabled={isReadOnly}
            />
          )}
        </ConfigAccordionHeader>
      </Box>
    </Box>
  );
};

interface RequirementAttachItemsTabProps {
  form: UseFormReturn<IAddRequirementsData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()(() => ({
  container: {
    display: "flex",
    flex: 1,
    height: "100%",
    overflow: "hidden",
    "& .MuiAccordion-root": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },
    "& .MuiAccordionDetails-root": {
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
    },
  },
  inner: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tabLabel: {
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: "bold",
    color: "text.primary",
  },
}));
